import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson } from '../utils/authClient.js';
import { AdminSection } from './AdminNav.jsx';
import { displayModelName } from '../pages/VeridionShared.jsx';

const ROLE_LABELS_DE = {
  coordinator: 'Koordinator & 10 KI-Kollegen (Standard-Arbeitsschicht)',
  call: 'Telefonie- & Konferenz-Gesprächslogik (Live-Anrufe)',
  architect: 'System- & Agenten-Architekt (Design & Spezifikation)',
  requirements_judge: 'Anforderungs- & IDD-Prüfer (Requirements Judge)',
  judge: 'Qualitäts- & Benchmark-Prüfer (Evaluation Judge)',
  ear: 'Konferenz-Protokollierung & aktive Gesprächserfassung',
  stt: 'Spracherkennung (Speech-to-Text / Audio-Transkription)',
};

const ROLE_LABELS_EN = {
  coordinator: 'Coordinator & 10 AI Colleagues (Primary Workforce Layer)',
  call: 'Telephony & Conference Dialogue Engine (Live Calls)',
  architect: 'System & Agent Architect (Design & Specification)',
  requirements_judge: 'Requirements & IDD Verifier (Requirements Judge)',
  judge: 'Quality & Benchmark Verifier (Evaluation Judge)',
  ear: 'Conference Transcription & Active Meeting Listener',
  stt: 'Speech Recognition (Speech-to-Text / Audio Transcription)',
};

const SystemLlmAllocationPanel = ({ compact = false }) => {
  const { currentLang } = useTranslation();
  const isDe = currentLang === 'de';
  const roleLabels = isDe ? ROLE_LABELS_DE : ROLE_LABELS_EN;
  const [modelsData, setModelsData] = useState(null);
  const [chosen, setChosen] = useState({});
  const [busyRole, setBusyRole] = useState('');
  const [notice, setNotice] = useState('');

  const loadModels = useCallback(async () => {
    try {
      const res = await requestJson('/api/admin/veridion-core/models');
      setModelsData(res);
      const nextChosen = {};
      Object.keys(ROLE_LABELS_DE).forEach((r) => {
        nextChosen[r] = res?.roles?.[r]?.chosen || '';
      });
      setChosen(nextChosen);
    } catch {
      // Fallback to static verified defaults if API is momentarily loading
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const saveRoleModel = async (role) => {
    setBusyRole(role);
    setNotice('');
    try {
      await requestJson('/api/admin/veridion-core/models', {
        method: 'POST',
        body: JSON.stringify({ role, model: chosen[role] || '' }),
      });
      await loadModels();
      setNotice(
        isDe
          ? `Modellzuordnung für „${ROLE_LABELS_DE[role] || role}“ erfolgreich aktualisiert.`
          : `Model assignment for "${ROLE_LABELS_EN[role] || role}" successfully updated.`
      );
    } catch (err) {
      setNotice(`${isDe ? 'Fehler beim Speichern' : 'Failed to save'}: ${err?.code || err?.message || '-'}`);
    } finally {
      setBusyRole('');
    }
  };

  const coordinatorModel = modelsData?.roles?.coordinator?.model || 'google/gemini-4-argon';
  const architectModel = modelsData?.roles?.architect?.model || 'deepseek/deepseek-v4.1-flash';
  const reqJudgeModel = modelsData?.roles?.requirements_judge?.model || 'deepseek/deepseek-v4.1-flash';
  const callModel = modelsData?.roles?.call?.model || 'openai/gpt-audio';
  const sttModel = modelsData?.roles?.stt?.model || 'google/gemini-3.5-flash';
  const availableModels = modelsData?.models || [
    'deepseek/deepseek-v4.1-flash',
    'deepseek/deepseek-v4-pro-0813',
    'Qwen/Qwen3-VL-235B-A22B-Instruct-FP8',
    'google/gemini-4-argon',
    'google/gemini-3.5-flash',
    'openai/gpt-6-luna',
    'openai/gpt-audio',
  ];

  // The system-wide allocation after D-SAAS-123: the whole vLLM serving (Silver, STT, TTS) is
  // retired for the training window — every consumer runs through the Envoy AI Gateway against
  // the cloud; FinBERT is the one standing local inference beside training.
  const systemConsumers = [
    {
      id: 'veridion-colleagues',
      component: isDe
        ? '1. Veridion Core — Koordinator & 10 KI-Kollegen (Tagesgeschäft in Webex & Sprints)'
        : '1. Veridion Core — Coordinator & 10 AI Colleagues (Daily Webex & Sprint Operations)',
      activeModel: `${displayModelName(coordinatorModel)} (${coordinatorModel}) · deepseek/deepseek-v4.1-flash`,
      backend: isDe ? 'OpenRouter Cloud (ersetzt vLLM-Silver)' : 'OpenRouter Cloud (replaces vLLM-Silver)',
      backendTone: 'is-warning',
      previousState: isDe
        ? 'Ehemals lokal auf vLLM-Silver -> fürs Trainingsfenster abgeschaltet (Replicas 0/0, Route umgeleitet)'
        : 'Previously local on vLLM-Silver -> retired for the training window (replicas 0/0, route repointed)',
      vramAndImpact: isDe
        ? '0 MiB lokales VRAM; Rückweg ist ein Replica-Schalter (D-PROD-053)'
        : '0 MiB local VRAM; the way back is a replica switch (D-PROD-053)',
    },
    {
      id: 'veridion-gold-escalation',
      component: isDe
        ? '2. Veridion Core — Architektur-Entwurf, Requirements-Judge & Eskalations-Spur (ehemals Lane Gold)'
        : '2. Veridion Core — Architecture Design, Requirements Judge & Escalation Lane (formerly Lane Gold)',
      activeModel: `${displayModelName(architectModel)} (${architectModel}) & ${displayModelName(reqJudgeModel)}`,
      backend: isDe ? 'OpenRouter Cloud (ersetzt vLLM-Gold)' : 'OpenRouter Cloud (replaces vLLM-Gold)',
      backendTone: 'is-warning',
      previousState: isDe
        ? 'Ehemals lokal auf vLLM-Gold -> lokal abgeschaltet (Replicas 0/0)'
        : 'Previously local on vLLM-Gold -> retired locally (replicas 0/0)',
      vramAndImpact: isDe
        ? 'Teil des freien 95,0-GB-Trainingsfensters'
        : 'Part of the free 95.0 GB training window',
    },
    {
      id: 'veridion-paioneer-orchestrator',
      component: isDe
        ? '3. Veridion pAIoneer Labs Orchestrator (Webex-Bridge, YouTrack- & MCP-Werkzeugsteuerung)'
        : '3. Veridion pAIoneer Labs Orchestrator (Webex Bridge, YouTrack & MCP Tool Orchestration)',
      activeModel: 'deepseek/deepseek-v4.1-flash',
      backend: isDe ? 'OpenRouter Cloud' : 'OpenRouter Cloud',
      backendTone: 'is-warning',
      previousState: isDe
        ? 'Silver- und Gold-Routen im Envoy AI Gateway transparent auf OpenRouter umgestellt'
        : 'Silver and Gold routes in the Envoy AI Gateway transparently repointed to OpenRouter',
      vramAndImpact: isDe
        ? '0 MiB lokales VRAM; Budgets & Buchung unverändert am Gateway'
        : '0 MiB local VRAM; budgets & metering unchanged at the gateway',
    },
    {
      id: 'veridion-coding-opencode',
      component: isDe
        ? '4. Veridion Coding-Agent (opencode — Autonome Softwareentwicklung & CI/CD in GitLab)'
        : '4. Veridion Coding Agent (opencode — Autonomous Software Engineering & CI/CD in GitLab)',
      activeModel: 'google/gemini-4-argon + deepseek/deepseek-v4.1-flash (OpenRouter)',
      backend: isDe ? 'OpenRouter Cloud' : 'OpenRouter Cloud',
      backendTone: 'is-ok',
      previousState: isDe
        ? 'Cloud-Modell unverändert; lokale Silver-Anteile folgen der Routen-Umleitung'
        : 'Cloud model unchanged; local silver shares follow the route repointing',
      vramAndImpact: isDe
        ? 'Keine VRAM-Belegung auf der RTX PRO 6000 Blackwell'
        : 'Zero VRAM footprint on the RTX PRO 6000 Blackwell',
    },
    {
      id: 'veridion-smtm-trading',
      component: isDe
        ? '5. ShowMeTheMoney (SMTM) Trading-Assistent & FinBERT Markt-Sentiment'
        : '5. ShowMeTheMoney (SMTM) Trading Assistant & FinBERT Market Sentiment',
      activeModel: isDe
        ? 'deepseek/deepseek-v4.1-flash (Chat/Broker-Tools) + ProsusAI/finbert (Sentiment, lokal)'
        : 'deepseek/deepseek-v4.1-flash (Chat/Broker Tools) + ProsusAI/finbert (Sentiment, local)',
      backend: isDe ? 'OpenRouter Cloud + Lokale GPU (nur FinBERT)' : 'OpenRouter Cloud + Local GPU (FinBERT only)',
      backendTone: 'is-ok',
      previousState: isDe
        ? 'Chat folgt der Workforce in die Cloud; FinBERT läuft lokal unverändert weiter'
        : 'Chat follows the workforce to the cloud; FinBERT keeps running locally unchanged',
      vramAndImpact: isDe
        ? '2.202 MiB VRAM für FinBERT · einziger stehender lokaler Inferenz-Dienst'
        : '2,202 MiB VRAM for FinBERT · the one standing local inference service',
    },
    {
      id: 'agentic-rag-saas',
      component: isDe
        ? '6. Agentic RAG Wissensdatenbank (Dokumenten-Synthese & Scan-Transkription)'
        : '6. Agentic RAG Knowledge Base (Document Synthesis & Scan Transcription)',
      activeModel: isDe
        ? 'deepseek/deepseek-v4.1-flash (Chat) · Qwen3-VL-235B (Scans, souveräne Cloud-Route) · text-embedding-3-small'
        : 'deepseek/deepseek-v4.1-flash (chat) · Qwen3-VL-235B (scans, sovereign cloud route) · text-embedding-3-small',
      backend: isDe ? 'Cloud über Envoy AI Gateway' : 'Cloud via Envoy AI Gateway',
      backendTone: 'is-warning',
      previousState: isDe
        ? 'Ehemals vLLM-Silver (Chat + Vision) -> Gateway-Routen zu OpenRouter/souveräner Cloud'
        : 'Previously vLLM-Silver (chat + vision) -> gateway routes to OpenRouter/sovereign cloud',
      vramAndImpact: isDe
        ? '0 MiB lokales VRAM'
        : '0 MiB local VRAM',
    },
    {
      id: 'voice-pipeline',
      component: isDe
        ? '7. Telefonie, Stundensitzungen & Webex-Sprach-Pipeline (STT & Stimmen)'
        : '7. Telephony, Hourly Meetings & Webex Voice Pipeline (STT & Voices)',
      activeModel: isDe
        ? `STT: ${sttModel} · Stimmen: Cloud-Sprecher des Audio-Modells (${callModel})`
        : `STT: ${sttModel} · Voices: the audio model's cloud speakers (${callModel})`,
      backend: isDe ? 'OpenRouter Cloud (Audio-Modelle)' : 'OpenRouter Cloud (audio models)',
      backendTone: 'is-warning',
      previousState: isDe
        ? 'vLLM-STT & vLLM-Omni-TTS abgeschaltet; Stimm-Clips bleiben gespeichert für die Rückkehr der Engine'
        : 'vLLM-STT & vLLM-Omni-TTS retired; voice clips stay stored for the engine\'s return',
      vramAndImpact: isDe
        ? '0 MiB lokales VRAM (vorher 15.924 MiB)'
        : '0 MiB local VRAM (previously 15,924 MiB)',
    },
    {
      id: 'datalake-factory',
      component: isDe
        ? '8. pAIoneer-Data-Lake — Trainingsdaten-Fabrik (Lehrer-Extraktion, Captions, Synthese)'
        : '8. pAIoneer-Data-Lake — Training Data Factory (teacher extraction, captions, synthesis)',
      activeModel: isDe
        ? 'Lehrer: deepseek-v4.1-flash, deepseek-v4-pro · Captions/Vision: Qwen3-VL-235B (souveräne Cloud-Route)'
        : 'Teachers: deepseek-v4.1-flash, deepseek-v4-pro · captions/vision: Qwen3-VL-235B (sovereign cloud route)',
      backend: isDe ? 'Cloud über Envoy AI Gateway' : 'Cloud via Envoy AI Gateway',
      backendTone: 'is-ok',
      previousState: isDe
        ? 'Silver-Lehrer ersetzt; Audio-Transkription pausiert ehrlich (Layer R, awaiting_stt)'
        : 'Silver teacher replaced; audio transcription paused honestly (Layer R, awaiting_stt)',
      vramAndImpact: isDe
        ? '0 GB lokales VRAM; Spend läuft gebucht über das Gateway (Owner steuert am OpenRouter-Guthaben)'
        : '0 GB local VRAM; spend metered at the gateway (owner steers at the OpenRouter balance)',
    },
    {
      id: 'sovereign-llm-training',
      component: isDe
        ? '9. Souveränes Eigenmodell-Training — zwei Stränge (Dokumenten-Strang trainiert zuerst)'
        : '9. Sovereign In-House Model Training — two strands (the document strand trains first)',
      activeModel: isDe
        ? 'Trainiert: pAIoneer-Scribe-7B-DocVision (Text+Bild→Text) · bereitet vor: pAIoneer-Omni-14B-A4B-MPO'
        : 'Training: pAIoneer-Scribe-7B-DocVision (text+image→text) · preparing: pAIoneer-Omni-14B-A4B-MPO',
      backend: isDe ? 'Dediziertes Lokales Trainings-VRAM' : 'Dedicated Local Training VRAM',
      backendTone: 'is-ok',
      previousState: isDe
        ? 'Das gesamte vLLM-Serving wurde für das Trainingsfenster abgeschaltet (D-SAAS-123)'
        : 'The whole vLLM serving was retired for the training window (D-SAAS-123)',
      vramAndImpact: isDe
        ? 'Gemessenes Fenster: 95.037 MiB (~95,0 GB) frei · nur FinBERT (2.202 MiB) daneben'
        : 'Measured window: 95,037 MiB (~95.0 GB) free · only FinBERT (2,202 MiB) beside it',
    },
  ];

  return (
    <AdminSection
      id="system-llm-allocation"
      title={isDe
        ? 'Aktive LLM-Modellzuordnung im Gesamtsystem (Wer nutzt welches LLM — Cloud über das Gateway, die Karte trainiert)'
        : 'System-Wide Active LLM Allocation (Who Uses Which LLM — Cloud via the Gateway While the Card Trains)'}
      subtitle={isDe
        ? 'Vollständige Transparenz über alle Dienste, Bots, Agenten-Rollen und Trainings-Pipelines: Das gesamte vLLM-Serving (Silver, STT, TTS) wurde für das Trainingsfenster abgeschaltet (Replicas 0/0, Infrastruktur erhalten, D-SAAS-123/D-PROD-053). Jeder Verbraucher läuft über das Envoy AI Gateway gegen die Cloud; FinBERT (2.202 MiB) ist die einzige stehende lokale Inferenz. Gemessenes Trainingsfenster: 95.037 MiB (~95,0 GB).'
        : 'Complete transparency across all services, bots, agent roles, and training pipelines: the whole vLLM serving (silver, STT, TTS) is retired for the training window (replicas 0/0, infrastructure kept, D-SAAS-123/D-PROD-053). Every consumer runs through the Envoy AI Gateway against the cloud; FinBERT (2,202 MiB) is the one standing local inference. Measured training window: 95,037 MiB (~95.0 GB).'}
      state={isDe ? 'vLLM-Serving: abgeschaltet · Trainingsfenster: 95,0 GB gemessen frei' : 'vLLM serving: retired · training window: 95.0 GB measured free'}
      tone="ok"
    >
      <div className="grid gap-3 md:grid-cols-3 text-xs">
        <div className="homepage-muted-panel p-4 space-y-1">
          <div className="font-bold text-sm">
            {isDe ? '1. Cloud über das Envoy AI Gateway' : '1. Cloud via the Envoy AI Gateway'}
          </div>
          <div>
            {isDe ? 'Arbeitsmodell: ' : 'Workforce model: '}
            <strong>deepseek/deepseek-v4.1-flash</strong>
          </div>
          <div>
            {isDe ? 'Vision & Audio: ' : 'Vision & audio: '}
            <strong>Qwen3-VL-235B · gemini-3.5-flash (STT) · gpt-audio ({isDe ? 'Stimmen' : 'voices'})</strong>
          </div>
          <div className="text-text-secondary">
            {isDe
              ? 'Kollegen, SMTM-Chat, Orchestrator, RAG, Research und die Daten-Fabrik laufen identitäts-budgetiert über die eine Gateway-Tür.'
              : 'Colleagues, SMTM chat, the orchestrator, RAG, research and the data factory run identity-budgeted through the one gateway door.'}
          </div>
        </div>
        <div className="homepage-muted-panel p-4 space-y-1">
          <div className="font-bold text-sm">
            {isDe ? '2. Lokale GPU: nur FinBERT steht' : '2. Local GPU: only FinBERT stands'}
          </div>
          <div>
            {isDe ? 'Aktives Modell: ' : 'Active model: '}
            <strong>ProsusAI/finbert</strong>
          </div>
          <div>
            {isDe ? 'VRAM-Belegung: ' : 'VRAM footprint: '}
            <strong>{isDe ? '2.202 MiB von 97.887 MiB' : '2,202 MiB of 97,887 MiB'}</strong>
          </div>
          <div className="text-text-secondary">
            {isDe
              ? 'SMTM-Nachrichten-Sentiment bleibt lokal; die vLLM-Deployments stehen auf 0 Replicas und kommen per Schalter zurück.'
              : 'SMTM news sentiment stays local; the vLLM deployments stand at 0 replicas and return by a switch.'}
          </div>
        </div>
        <div className="homepage-muted-panel p-4 space-y-1">
          <div className="font-bold text-sm">
            {isDe ? '3. Gemessenes Trainingsfenster (95,0 GB)' : '3. Measured training window (95.0 GB)'}
          </div>
          <div>
            {isDe ? 'Frei gemessen: ' : 'Measured free: '}
            <strong>{isDe ? '95.037 MiB (~95,0 GB)' : '95,037 MiB (~95.0 GB)'}</strong>
          </div>
          <div>
            {isDe ? 'Trainiert zuerst: ' : 'Trains first: '}
            <strong>pAIoneer-Scribe-7B-DocVision ({isDe ? 'Bedarf 60,0 GB' : 'needs 60.0 GB'})</strong>
          </div>
          <div className="text-text-secondary">
            {isDe
              ? 'Der Any-to-Any-Strang (pAIoneer-Omni) sammelt und bereitet weiter vor, startet aber nicht, solange der Dokumenten-Strang ausgewählt ist.'
              : 'The any-to-any strand (pAIoneer-Omni) keeps collecting and preparing but never starts while the document strand is selected.'}
          </div>
        </div>
      </div>

      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '24%' }}>{isDe ? 'Systemkomponente / Dienst / KI-Einheit' : 'System Component / Service / AI Unit'}</th>
                <th style={{ width: '24%' }}>{isDe ? 'Aktuell genutztes LLM (Live)' : 'Currently Active LLM (Live)'}</th>
                <th style={{ width: '16%' }}>{isDe ? 'Ausführungsort' : 'Execution Backend'}</th>
                <th style={{ width: '18%' }}>{isDe ? 'Änderung gegenüber früherem Gold-Betrieb' : 'Change vs. Former Gold Setup'}</th>
                <th style={{ width: '18%' }}>{isDe ? 'VRAM-Belegung & Betriebsstatus' : 'VRAM Impact & Status'}</th>
              </tr>
            </thead>
            <tbody>
              {systemConsumers.map((row) => (
                <tr key={row.id}>
                  <td className="font-bold text-xs">{row.component}</td>
                  <td className="font-mono text-xs">{row.activeModel}</td>
                  <td>
                    <span className={`admin-status-pill ${row.backendTone}`}>{row.backend}</span>
                  </td>
                  <td className="text-xs text-text-secondary">{row.previousState}</td>
                  <td className="text-xs">{row.vramAndImpact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!compact && modelsData?.roles ? (
        <div className="homepage-muted-panel p-4 space-y-3">
          <div className="font-bold text-sm">
            {isDe
              ? 'Direkte Modell-Steuerung pro Veridion-Core-Rolle (Live-Umschaltung zwischen lokaler GPU & OpenRouter)'
              : 'Direct Model Control per Veridion Core Role (Live Switch Between Local GPU & OpenRouter)'}
          </div>
          <div className="text-xs text-text-secondary">
            {isDe
              ? 'Hier sehen und ändern Sie direkt, welches konkret geroutete Modell jede einzelne Rolle in Veridion Core verwendet. Standardmäßig nutzt das Tagesgeschäft lokal Qwen3.8-27B-silver, während Architekt und Requirements-Judge auf OpenRouter (deepseek/deepseek-v4.1-flash) laufen.'
              : 'Inspect and switch the exact model routed for each Veridion Core role. By default, daily workforce operations run locally on Qwen3.8-27B-silver while Architect and Requirements Judge run on OpenRouter (deepseek/deepseek-v4.1-flash).'}
          </div>
          {notice ? <div className="text-xs font-bold">{notice}</div> : null}
          <div className="admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{isDe ? 'Veridion-Core-Rolle' : 'Veridion Core Role'}</th>
                    <th>{isDe ? 'Aktives Modell (Effektiv)' : 'Active Model (Effective)'}</th>
                    <th>{isDe ? 'System-Standard' : 'System Default'}</th>
                    <th>{isDe ? 'Modellauswahl (Lokal GPU / OpenRouter)' : 'Select Model (Local GPU / OpenRouter)'}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(ROLE_LABELS_DE).map((role) => {
                    const entry = modelsData.roles?.[role] || {};
                    return (
                      <tr key={role}>
                        <td className="font-bold text-xs">{roleLabels[role] || role}</td>
                        <td className="font-mono text-xs">{displayModelName(entry.model) || entry.model || '-'}</td>
                        <td className="text-xs text-text-secondary">{displayModelName(entry.default) || entry.default || '-'}</td>
                        <td>
                          <select
                            className="form-input text-xs"
                            value={chosen[role] ?? ''}
                            onChange={(e) => setChosen({ ...chosen, [role]: e.target.value })}
                          >
                            <option value="">{isDe ? 'System-Standard verwenden' : 'Use System Default'}</option>
                            {availableModels.map((mName) => (
                              <option key={mName} value={mName}>
                                {displayModelName(mName)} ({mName.includes('/') ? 'OpenRouter Cloud' : (isDe ? 'Lokale GPU' : 'Local GPU')})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="homepage-button-secondary text-xs"
                            disabled={busyRole === role || (chosen[role] ?? '') === (entry.chosen || '')}
                            onClick={() => saveRoleModel(role)}
                          >
                            {busyRole === role ? '...' : (isDe ? 'Übernehmen' : 'Apply')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </AdminSection>
  );
};

export default SystemLlmAllocationPanel;
